import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    quickLinksGridItem?: GridItemProps;
    ServicesGridItem?: GridItemProps;
    keyWholesaleGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        css: css`
            margin: auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-right: 1rem;
                            padding-left: 1rem;
                            max-width: 100%;
                        `,
                        null,
                        null,
                        css`
                            padding-right: 5rem;
                            padding-left: 5rem;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    logoGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            > div {
                width: 100%;
                .wrapper {
                    max-width: 100%;
                    max-height: 100%;
                }
            }
        `,
    },
    quickLinksGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            .list-wrapper {
                padding: 0;
            }
            .title-wrapper {
                [class^="TypographyStyle"] {
                    text-transform: capitalize;
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin-bottom: 20px;
                    color: ${getColor("primary.contrast")};
                }
                &:hover {
                    cursor: default;
                    [class^="TypographyStyle"] {
                        color: ${getColor("primary.contrast")};
                        cursor: default;
                    }
                }
            }

            .list-link {
                [class^="TypographyStyle"] {
                    text-transform: capitalize;
                    font-size: 1rem;
                    font-weight: 600;
                    margin-bottom: 15px;
                    color: ${getColor("primary.contrast")};
                }
            }
        `,
    },
    ServicesGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            .list-wrapper {
                padding: 0;
            }
            .title-wrapper {
                [class^="TypographyStyle"] {
                    text-transform: capitalize;
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin-bottom: 20px;
                    color: ${getColor("primary.contrast")};
                }
                &:hover {
                    cursor: default;
                    [class^="TypographyStyle"] {
                        color: ${getColor("primary.contrast")};
                        cursor: default;
                    }
                }
            }

            .list-link {
                [class^="TypographyStyle"] {
                    text-transform: capitalize;
                    font-size: 1rem;
                    font-weight: 600;
                    margin-bottom: 15px;
                    color: ${getColor("primary.contrast")};
                }
            }
        `,
    },
    keyWholesaleGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            align-items: flex-end;
                        `,
                    ],
                    "min",
                )}
            > div:first-child {
                .wrapper {
                    max-width: 100%;
                }
                margin-bottom: 7px;
            }
            > div {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                text-align: center;
                            `,
                        ],
                        "max",
                    )}
            }
            .social-links {
                justify-content: center;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                gap: 14px;
                            `,
                            null,
                            css`
                                gap: 8px;
                            `,
                            null,
                            css`
                                gap: 19px;
                            `,
                        ],
                        "min",
                    )}
                .link-wrapper {
                    margin-right: 0;
                    flex-grow: 0;
                }
            }
            .list-wrapper {
                margin-top: 7px;
            }
            .title-wrapper {
                [class^="StyledButton"] {
                    [class^="TypographyStyle"] {
                        text-transform: capitalize;
                        font-size: 1.5rem;
                        font-weight: 700;
                        margin-bottom: 20px;
                        color: ${getColor("primary.contrast")};
                    }
                    &:hover {
                        cursor: default;
                        [class^="TypographyStyle"] {
                            color: ${getColor("primary.contrast")};
                            cursor: default;
                        }
                    }
                }
            }

            .list-link {
                [class^="TypographyStyle"] {
                    text-transform: capitalize;
                    font-size: 1rem;
                    font-weight: 600;
                    margin-bottom: 15px;
                    color: ${getColor("primary.contrast")};
                }
            }
        `,
    },
    socialLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
        css: css`
            border-top: 1px solid #e6e1e7;
            text-align: center;
            color: ${getColor("primary.contrast")};
            .wrapper {
                pre {
                    white-space: pre-wrap;
                    span {
                        font-family: inherit;
                        font-weight: 400;
                        font-size: 14px;
                    }
                }
            }
        `,
    },
    logoWrapper: {
        css: css`
            width: 110px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            text-align: center;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    return (
        <GridContainer {...styles.widgetGridContainer}>
            <GridItem {...styles.logoGridItem}>
                <StyledWrapper {...styles.logoWrapper}>
                    <Zone zoneName="Logo" contentId={id} />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.quickLinksGridItem}>
                <Zone zoneName="QuickLinks" contentId={id} />
            </GridItem>
            <GridItem {...styles.ServicesGridItem}>
                <Zone zoneName="Services" contentId={id} />
            </GridItem>
            <GridItem {...styles.keyWholesaleGridItem}>
                <Zone zoneName="KeyWholeSaleLogo" contentId={id} />
                <Zone zoneName="SocialLinks" contentId={id} />
            </GridItem>
            <GridItem {...styles.richContentGridItem}>
                <Zone zoneName="RichContent" contentId={id} />
            </GridItem>
        </GridContainer>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
